import React, { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import MarkdownEditor from "../components/MarkdownEditor";
import NewTicketModal from "../components/NewTicketModal";
import PredefinedReplySelector from "../components/PredefinedReplySelector";
import SendEmailModal from "../components/SendEmailModal";
import StatusBadge from "../components/StatusBadge";
import TicketPresenceBanner from "../components/tickets/TicketPresenceBanner";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import useImpersonationGuard from "../hooks/useImpersonationGuard";
import useTicketPresence from "../hooks/useTicketPresence";
import apiService from "../services/api";
import parseMarkdown from "../utils/parseMarkdown";

// Helper function to get file icon based on extension
const getFileIcon = (extension) => {
  const ext = extension.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "fa-image";
  if (["pdf"].includes(ext)) return "fa-file-pdf";
  if (["doc", "docx"].includes(ext)) return "fa-file-word";
  if (["txt"].includes(ext)) return "fa-file-alt";

  return "fa-file";
};

// Helper function to check if file is an image
const isImageFile = (extension) => {
  const ext = extension?.toLowerCase()?.replace(".", "");

  return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext);
};

const getAuthorDisplayName = (author) =>
  author?.name ||
  author?.fullName ||
  author?.full_name ||
  author?.displayName ||
  author?.display_name ||
  (author?.email ? author.email.split("@")[0] : null) ||
  "Unknown User";

const getAuthorInitials = (author) => {
  const source = getAuthorDisplayName(author);

  if (!source || source === "Unknown User") return "?";

  const initials = source
    .split(/\s+/)
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return initials || "?";
};

const CommentAvatar = ({ author, isAdmin }) => {
  const url = author?.avatarUrl || author?.avatar_url;
  const name = getAuthorDisplayName(author);
  const gradient = isAdmin
    ? "from-primary-500 to-primary-700"
    : "from-blue-500 to-blue-700";

  if (url) {
    return (
      <img
        alt={name}
        className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200 dark:ring-white/10 shrink-0"
        src={url}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-sm font-semibold ring-2 ring-gray-200 dark:ring-white/10 shrink-0`}
    >
      {getAuthorInitials(author)}
    </div>
  );
};

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { guardImpersonatedAction, isImpersonating } = useImpersonationGuard();
  const { checkNewTickets, markAsReadByTicketId } = useNotifications();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [isSendingComment, setIsSendingComment] = useState(false);
  const textareaRef = React.useRef(null);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [_uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = React.useRef(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const statusDropdownRef = React.useRef(null);
  const assignDropdownRef = React.useRef(null);
  const [isUserActive, setIsUserActive] = useState(true);
  const pollingIntervalRef = React.useRef(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [commentHistory, setCommentHistory] = useState([]);
  const cooldownTimerRef = React.useRef(null);
  const [returnToTickets, setReturnToTickets] = useState(false);
  const [customerTicketCount, setCustomerTicketCount] = useState(null);
  const typingTimerRef = React.useRef(null);
  const { viewers: presenceViewers, setTyping: setPresenceTyping } =
    useTicketPresence(ticket?._id, {
      onJoin: (viewer) => {
        toast.info(`${viewer.name} is also viewing this ticket`);
      },
    });

  const handleCommentChange = (next) => {
    setComment(next);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (!next || !next.trim()) {
      setPresenceTyping(false);

      return;
    }
    setPresenceTyping(true);
    typingTimerRef.current = setTimeout(() => {
      setPresenceTyping(false);
    }, 3000);
  };

  useEffect(() => {
    fetchTicket();
    fetchAdmins();
    loadCommentHistory();
  }, [id, token]); // Add token as dependency to refetch when auth changes

  // Load comment history from localStorage
  const loadCommentHistory = () => {
    const userId = user?.id || user?.email || "anonymous";
    const stored = localStorage.getItem(`ticket_comments_${id}_${userId}`);

    if (stored) {
      try {
        const data = JSON.parse(stored);
        // Clean up old entries (older than 5 minutes)
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        const recentComments = data.filter(
          (timestamp) => timestamp > fiveMinutesAgo
        );

        setCommentHistory(recentComments);

        // Calculate if there's an active cooldown
        if (recentComments.length > 0) {
          const lastComment = recentComments[recentComments.length - 1];
          const timeSinceLastComment = Date.now() - lastComment;
          const cooldownDuration = getCooldownDuration(recentComments.length);

          if (timeSinceLastComment < cooldownDuration) {
            setCooldownRemaining(
              Math.ceil((cooldownDuration - timeSinceLastComment) / 1000)
            );
          }
        }
      } catch (e) {
        console.error("Failed to load comment history:", e);
      }
    }
  };

  // Get cooldown duration based on comment frequency
  const getCooldownDuration = (recentCommentCount) => {
    // Admins have no cooldown
    if (user?.role === "admin") {
      return 0;
    }

    // Progressive cooldown for regular users
    if (recentCommentCount === 0) {
      return 0; // First comment, no cooldown
    } else if (recentCommentCount === 1) {
      return 30 * 1000; // 30 seconds after first comment
    } else if (recentCommentCount === 2) {
      return 60 * 1000; // 60 seconds after second comment
    } else {
      return 90 * 1000; // 90 seconds for subsequent comments
    }
  };

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownRemaining > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldownRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current);

            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      return () => {
        if (cooldownTimerRef.current) {
          clearInterval(cooldownTimerRef.current);
        }
      };
    }
  }, [cooldownRemaining]);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusMenu(false);
      }
      if (
        assignDropdownRef.current &&
        !assignDropdownRef.current.contains(event.target)
      ) {
        setShowAssignMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-polling for ticket updates
  useEffect(() => {
    // Poll every 10 seconds when user is active and not typing
    const POLLING_INTERVAL = 10000; // 10 seconds

    const startPolling = () => {
      pollingIntervalRef.current = setInterval(() => {
        // Only poll if user is active and not typing in comment field
        if (isUserActive && !comment && !editCommentText) {
          fetchTicket(true); // Pass true to indicate this is a polling request
        }
      }, POLLING_INTERVAL);
    };

    // Start polling
    startPolling();

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [id, isUserActive, comment, editCommentText]);

  // Fetch lifetime ticket count for the customer (used by the Ticket History pill)
  useEffect(() => {
    if (user?.role !== "admin") return;
    const regId = ticket?.registration_id || ticket?.customer?.registration_id;

    if (!regId) {
      setCustomerTicketCount(null);

      return;
    }

    let cancelled = false;

    apiService
      .getTickets({ registration_id: regId, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const total =
          res?.pagination?.total ??
          (Array.isArray(res?.tickets) ? res.tickets.length : null);

        setCustomerTicketCount(typeof total === "number" ? total : null);
      })
      .catch(() => {
        if (!cancelled) setCustomerTicketCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role, ticket?.registration_id, ticket?.customer?.registration_id]);

  // Detect user activity
  useEffect(() => {
    let inactivityTimer;
    const INACTIVITY_TIMEOUT = 60000; // 1 minute

    const resetInactivityTimer = () => {
      setIsUserActive(true);
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        setIsUserActive(false);
      }, INACTIVITY_TIMEOUT);
    };

    // Listen for user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => {
      document.addEventListener(event, resetInactivityTimer);
    });

    // Initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((event) => {
        document.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, []);

  const fetchTicket = async (isPolling = false) => {
    if (!id || id === "undefined") {
      // No valid ticket ID provided
      if (!isPolling) {
        setLoading(false);
        setTicket(null);
      }

      return;
    }

    if (!isPolling) {
      setLoading(true);
    }

    try {
      const response = await apiService.getTicket(id);
      const data = response.data || response; // Handle both wrapped and unwrapped responses

      // Transform the API response to match frontend expectations
      const transformedTicket = {
        _id: data.ticket_id,
        ticketNumber: data.ticket_number,
        subject: data.subject,
        description: data.description,
        status: data.status.replace("_", "-"), // Convert customer_replied to customer-replied
        priority: data.priority,
        category: data.category,
        customer: data.customer,
        assignedTo: data.assignedTo,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        resolvedAt: data.resolved_at,
        comments: data.comments || [],
        attachments: data.attachments || [],
        metadata: data.metadata || {},
        registration_id: data.registration_id, // Include registration_id from API
        user_id: data.user_id, // Include user_id from API
      };

      setTicket(transformedTicket);

      // Mark notifications for this ticket as read (only on initial load, not polling)
      if (!isPolling) {
        // Use the ticket number/ID to mark notifications as read
        await markAsReadByTicketId(data.ticket_number || id);
      }

      // Refresh the tickets count since this ticket has been viewed
      if (user?.role === "admin") {
        setTimeout(() => {
          checkNewTickets();
        }, 500);
      }
    } catch (error) {
      // Error fetching ticket
      if (!isPolling) {
        toast.error("Failed to load ticket");
        setTicket(null);
      }
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  };

  const fetchAdmins = async () => {
    try {
      // Fetch all admins for assignment dropdown
      const response = await apiService.get("/v1/dashboard/tickets/admins");

      if (response.data) {
        setAdmins(response.data);
      } else {
        setAdmins([]);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      // Error fetching admins - set empty array
      setAdmins([]);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    if (guardImpersonatedAction("Replying to tickets")) return;

    // Check cooldown
    if (cooldownRemaining > 0) {
      toast.warning(
        `Please wait ${cooldownRemaining} seconds before posting again`
      );

      return;
    }

    if (!id || id === "undefined") {
      toast.error("Invalid ticket ID");

      return;
    }

    // Start sending
    setIsSendingComment(true);

    try {
      // Upload files first if any are selected
      const uploadedAttachments = [];

      if (selectedFiles.length > 0) {
        setUploadingFiles(true);

        for (const file of selectedFiles) {
          try {
            const response = await apiService.uploadFile(file);

            if (response.data?.files && response.data.files[0]) {
              uploadedAttachments.push(response.data.files[0]);
            }
          } catch (fileError) {
            console.error("Error uploading file:", file.name, fileError);
            // Continue with other files even if one fails
          }
        }

        setUploadingFiles(false);
      }

      // Add comment with attachments
      await apiService.addTicketComment(id, {
        content: comment,
        attachments:
          uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      });

      // Update comment history
      const now = Date.now();
      const fiveMinutesAgo = now - 5 * 60 * 1000;
      const updatedHistory = [
        ...commentHistory.filter((t) => t > fiveMinutesAgo),
        now,
      ];

      setCommentHistory(updatedHistory);

      // Save to localStorage
      const userId = user?.id || user?.email || "anonymous";

      localStorage.setItem(
        `ticket_comments_${id}_${userId}`,
        JSON.stringify(updatedHistory)
      );

      // Set cooldown
      const cooldownDuration = getCooldownDuration(updatedHistory.length - 1);

      if (cooldownDuration > 0) {
        setCooldownRemaining(cooldownDuration / 1000);
      }

      // Clear comment and files
      setComment("");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Show success message
      const filesText =
        uploadedAttachments.length > 0
          ? ` with ${uploadedAttachments.length} file(s)`
          : "";

      toast.success(`Comment added successfully${filesText}`);

      // Small delay to show success state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Check if admin wants to return to tickets page
      if (returnToTickets && user?.role === "admin") {
        // Navigate back to tickets page
        navigate("/tickets");
      } else {
        // Reload ticket to get updated comments
        await fetchTicket();

        // Refresh notifications to update the count
        if (user?.role === "admin") {
          setTimeout(() => {
            checkNewTickets();
          }, 100);
        }
      }
    } catch (error) {
      // Error saving comment
      toast.error("Failed to add comment");
    } finally {
      // Stop sending
      setIsSendingComment(false);
      setUploadingFiles(false);
    }
  };

  const handleFileSelect = (e) => {
    if (guardImpersonatedAction("Attaching files to tickets")) {
      e.target.value = "";

      return;
    }

    const files = Array.from(e.target.files);

    setSelectedFiles(files);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleStatusChange = async (newStatus) => {
    if (guardImpersonatedAction("Changing ticket status")) return;

    const apiStatus = newStatus.replace("-", "_"); // Convert customer-replied to customer_replied

    try {
      await apiService.updateTicket(id, { status: apiStatus });

      // Update local state
      setTicket((prev) => ({ ...prev, status: newStatus }));
      setShowStatusMenu(false);

      toast.success("Status updated successfully");
    } catch (error) {
      // Error updating status
      toast.error("Failed to update status");
    }
  };

  const handleAssigneeChange = async (staffMember) => {
    if (guardImpersonatedAction("Changing ticket assignment")) return;

    try {
      await apiService.updateTicket(id, {
        assigned_to: staffMember?.id || null,
      });

      // Update local state
      setTicket((prev) => ({ ...prev, assignedTo: staffMember }));
      setShowAssignMenu(false);

      toast.success("Ticket assigned successfully");
    } catch (error) {
      // Error updating assignee
      toast.error("Failed to assign ticket");
    }
  };

  const handleAiToggle = async () => {
    if (guardImpersonatedAction("Changing ticket AI settings")) return;

    const current = ticket.metadata?.ai?.enabled !== false; // default ON
    const next = !current;
    const newMetadata = {
      ...(ticket.metadata || {}),
      ai: { ...((ticket.metadata || {}).ai || {}), enabled: next },
    };

    try {
      await apiService.updateTicket(id, { metadata: newMetadata });
      setTicket((prev) => ({ ...prev, metadata: newMetadata }));
      toast.success(
        next ? "AI auto-reply enabled" : "AI auto-reply paused for this ticket"
      );
    } catch (error) {
      toast.error("Failed to update AI setting");
    }
  };

  const handleEditComment = (commentId, currentText) => {
    setEditingComment(commentId);
    setEditCommentText(currentText || "");
  };

  const handleCancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText("");
  };

  const handleSaveEditComment = async (commentId) => {
    if (guardImpersonatedAction("Editing ticket comments")) return;

    const text = editCommentText.trim();

    if (!text) {
      toast.error("Comment cannot be empty");

      return;
    }

    try {
      const response = await apiService.updateTicketComment(id, commentId, {
        content: text,
      });

      const editedAt = response?.edited_at || new Date().toISOString();
      const updatedContent = response?.content ?? text;

      setTicket((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          comments: prev.comments.map((c) =>
            c.comment_id === commentId
              ? {
                  ...c,
                  content: updatedContent,
                  message: updatedContent,
                  metadata: {
                    ...(c.metadata || {}),
                    edited_at: editedAt,
                  },
                  updated_at: editedAt,
                }
              : c
          ),
        };
      });

      setEditingComment(null);
      setEditCommentText("");
      toast.success("Comment updated");
    } catch (err) {
      console.error("Failed to update comment:", err);
      toast.error(err?.message || "Failed to update comment");
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      technical: "fa-cog",
      billing: "fa-file-invoice-dollar",
      general: "fa-question-circle",
      "feature-request": "fa-lightbulb",
      bug: "fa-bug",
    };

    return icons[category] || "fa-ticket-alt";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 5) return "Just now";
    if (seconds < 60) return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

    // For older dates, show the full date and time
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">
          <i className="fas fa-spinner fa-spin text-4xl" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Ticket not found</p>
        <button
          className="btn btn-primary mt-4"
          onClick={() => navigate("/tickets")}
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            /* Hide navigation and buttons during print */
            .btn, button, nav, .sidebar, header {
              display: none !important;
            }
            
            /* Show main content only */
            .print\\:hidden {
              display: none !important;
            }
            
            /* Improve print layout */
            body {
              font-size: 12pt;
            }
            
            /* Remove backgrounds and shadows */
            * {
              box-shadow: none !important;
              text-shadow: none !important;
            }
            
            /* Ensure text is black on white for better printing */
            body, .dark {
              background: white !important;
              color: black !important;
            }
            
            .text-gray-500, .text-gray-600, .text-gray-700,
            .dark\\:text-gray-300, .dark\\:text-gray-400 {
              color: #4a4a4a !important;
            }
            
            /* Page breaks */
            .page-break-after {
              page-break-after: always;
            }
            
            /* Links */
            a[href]:after {
              content: none !important;
            }
          }
        `}
      </style>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-shrink-0">
              <h1 className="text-lg font-bold text-gray-700 dark:text-gray-300">
                {ticket.ticketNumber}
              </h1>
              <StatusBadge status={ticket.status} />
            </div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white text-right truncate">
              {ticket.subject}
            </h2>
          </div>
          <div className="flex justify-end">
            <button
              className="btn btn-ghost flex-shrink-0"
              onClick={handlePrint}
            >
              <i className="fas fa-print" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Reply Form */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md"
              initial={{ opacity: 0, y: 20 }}
            >
              <h3 className="font-semibold mb-4 pl-2.5 pt-2.5">Reply</h3>
              <TicketPresenceBanner viewers={presenceViewers} />
              <div className="pl-2.5">
                {isImpersonating && (
                  <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                    <i className="fas fa-user-shield mt-0.5" />
                    <div>
                      <p className="font-semibold">
                        Ticket replies are locked during impersonation.
                      </p>
                      <p>
                        Return to Admin before replying, closing tickets, or
                        changing support settings.
                      </p>
                    </div>
                  </div>
                )}
                <MarkdownEditor
                  disabled={isImpersonating}
                  placeholder="Add a comment... (Markdown formatting supported)"
                  rightSlot={() =>
                    user?.role === "admin" ? (
                      <PredefinedReplySelector
                        ticket={ticket}
                        onSelect={(content) => {
                          if (comment.trim()) {
                            setComment(`${comment}\n\n${content}`);
                          } else {
                            setComment(content);
                          }
                        }}
                      />
                    ) : null
                  }
                  textareaRef={textareaRef}
                  value={comment}
                  onChange={handleCommentChange}
                />

                {/* Cooldown message */}
                {cooldownRemaining > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <i className="fas fa-clock animate-pulse" />
                    <span>
                      Please wait {cooldownRemaining} seconds before posting
                      again
                    </span>
                    <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 transition-all duration-1000"
                        style={{
                          width: `${(cooldownRemaining / (getCooldownDuration(commentHistory.length - 1) / 1000)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                {commentHistory.length >= 3 &&
                  cooldownRemaining === 0 &&
                  user?.role === "user" && (
                    <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                      <i className="fas fa-info-circle mr-1" />
                      Rapid posting detected - cooldown periods are active
                    </div>
                  )}

                {/* Selected Files Display */}
                {selectedFiles.length > 0 && (
                  <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {selectedFiles.length} file(s) selected
                      </span>
                      <button
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                        type="button"
                        onClick={() => {
                          setSelectedFiles([]);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700"
                          key={index}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <i
                              className={`fas ${getFileIcon(file.name.split(".").pop())} text-gray-400`}
                            />
                            <span className="text-sm truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            className="ml-2 text-red-600 hover:text-red-700 dark:text-red-400"
                            type="button"
                            onClick={() => {
                              const newFiles = selectedFiles.filter(
                                (_, i) => i !== index
                              );

                              setSelectedFiles(newFiles);
                            }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File input hidden */}
                <input
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  className="hidden"
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                />

                {/* Bottom row with Attach Files and Send buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-3">
                  {/* Attach Files button */}
                  <button
                    className="btn btn-ghost text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isImpersonating}
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="fas fa-paperclip mr-2" />
                    Attach Files
                  </button>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {/* Return to tickets checkbox - Admin only */}
                    {user?.role === "admin" && (
                      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200 transition-colors whitespace-nowrap">
                        <input
                          checked={returnToTickets}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                          type="checkbox"
                          onChange={(e) => setReturnToTickets(e.target.checked)}
                        />
                        <span>Return to tickets page</span>
                      </label>
                    )}

                    {/* Send button */}
                    <button
                      className={`btn btn-primary pt-[10px] relative ${
                        cooldownRemaining > 0 ||
                        isSendingComment ||
                        isImpersonating
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={
                        isImpersonating ||
                        cooldownRemaining > 0 ||
                        !comment.trim() ||
                        isSendingComment
                      }
                      onClick={handleAddComment}
                    >
                      {cooldownRemaining > 0 ? (
                        <>
                          <i className="fas fa-clock" />
                          Wait {cooldownRemaining}s
                        </>
                      ) : isSendingComment ? (
                        <>
                          <span className="opacity-0">Send</span>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          </div>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Comments in Table Format */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-semibold mb-4 pl-2.5 pt-2.5">
                Activity & Comments
              </h3>

              {ticket.comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-comments text-4xl mb-2" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to add a comment!</p>
                </div>
              ) : (
                <div>
                  <div className="space-y-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto sm:overflow-x-visible">
                    {[...ticket.comments].reverse().map((comment, index) => {
                      const isEvenRow = index % 2 === 0;

                      // Determine background color with proper striping
                      let bgClass = "";

                      if (comment.is_internal) {
                        // Internal notes always yellow
                        bgClass = "bg-yellow-50 dark:bg-yellow-900/20";
                      } else if (isEvenRow) {
                        // Even rows - light gray
                        bgClass = "bg-gray-100/50 dark:bg-gray-800/50";
                      } else {
                        // Odd rows - white/default
                        bgClass = "bg-white dark:bg-gray-900";
                      }

                      const isAdminComment =
                        comment.author?.role === "admin" ||
                        comment.author_type === "admin" ||
                        !!comment.admin_id;

                      return (
                        <motion.div
                          animate={{ opacity: 1, x: 0 }}
                          className={`
                        relative p-4 transition-colors hover:bg-blue-50/40 dark:hover:bg-blue-900/20 sm:min-w-[600px]
                        ${bgClass}
                      `}
                          initial={{ opacity: 0, x: -20 }}
                          key={comment.comment_id}
                          transition={{ delay: index * 0.05 }}
                        >
                          {/* Content Container */}
                          <div className="flex gap-4">
                            <CommentAvatar
                              author={comment.author}
                              isAdmin={isAdminComment}
                            />
                            <div className="flex-1 min-w-0">
                              {/* Header with Author and Time */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    onClick={() => {
                                      const authorId = comment.author?.id;

                                      if (user && user.id === authorId) {
                                        navigate("/profile");
                                      } else if (authorId) {
                                        navigate(`/customers/${authorId}`);
                                      } else {
                                        console.error(
                                          "No valid user ID found in author:",
                                          comment.author
                                        );
                                        toast.error(
                                          "Unable to view user profile"
                                        );
                                      }
                                    }}
                                  >
                                    {comment.author?.name ||
                                      comment.author?.fullName ||
                                      comment.author?.full_name ||
                                      comment.author?.displayName ||
                                      comment.author?.display_name ||
                                      (comment.author?.email
                                        ? comment.author.email.split("@")[0]
                                        : null) ||
                                      "Unknown User"}
                                  </div>

                                  {comment.author?.role === "admin" ||
                                  comment.author_type === "admin" ||
                                  comment.admin_id ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 uppercase">
                                      Admin
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 uppercase">
                                      Owner
                                    </span>
                                  )}

                                  {comment.author_subtype === "ai" && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 uppercase">
                                      <i className="fas fa-moon mr-1" />
                                      AI · Luna
                                    </span>
                                  )}

                                  {comment.is_internal && (
                                    <span className="badge badge-warning text-xs">
                                      <i className="fas fa-lock mr-1" />
                                      Internal
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                  {comment.metadata?.edited_at && (
                                    <span
                                      className="italic"
                                      title={`Edited ${formatDate(comment.metadata.edited_at)}`}
                                    >
                                      (edited)
                                    </span>
                                  )}
                                  <span>
                                    {formatDate(
                                      comment.created_at || comment.createdAt
                                    )}
                                  </span>
                                  {user?.role === "admin" &&
                                    isAdminComment &&
                                    editingComment !== comment.comment_id && (
                                      <button
                                        className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                        title="Edit comment"
                                        type="button"
                                        onClick={() =>
                                          handleEditComment(
                                            comment.comment_id,
                                            comment.content ||
                                              comment.message ||
                                              ""
                                          )
                                        }
                                      >
                                        <i className="fas fa-pen" />
                                        <span>Edit</span>
                                      </button>
                                    )}
                                </div>
                              </div>

                              {/* Comment Content */}
                              {editingComment === comment.comment_id ? (
                                <div className="mb-4">
                                  <MarkdownEditor
                                    minHeightClass="min-h-[140px]"
                                    placeholder="Edit comment... (Markdown formatting supported)"
                                    value={editCommentText}
                                    onChange={setEditCommentText}
                                  />
                                  <div className="mt-2 flex items-center justify-end gap-2">
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      type="button"
                                      onClick={handleCancelEditComment}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="btn btn-primary btn-sm"
                                      disabled={!editCommentText.trim()}
                                      type="button"
                                      onClick={() =>
                                        handleSaveEditComment(
                                          comment.comment_id
                                        )
                                      }
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none break-words [&>*]:max-w-full [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-words [&_table]:table-fixed [&_table]:w-full mb-4"
                                  dangerouslySetInnerHTML={{
                                    __html: parseMarkdown(
                                      comment.content || comment.message || ""
                                    ),
                                  }}
                                />
                              )}

                              {/* Comment Attachments */}
                              {comment.attachments &&
                                comment.attachments.length > 0 && (
                                  <div className="mt-3 mb-4">
                                    {/* Separator line */}
                                    <div className="w-full h-px bg-gray-200 dark:bg-gray-700 mb-3" />

                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                      {comment.attachments.length} attachment(s)
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {comment.attachments.map(
                                        (attachment, attIndex) => {
                                          const extension =
                                            attachment.extension?.replace(
                                              ".",
                                              ""
                                            ) ||
                                            attachment.name?.split(".").pop();
                                          const isImage =
                                            isImageFile(extension);

                                          return (
                                            <div
                                              className="relative group cursor-pointer border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-primary-500 transition-colors w-[90px] h-[90px]"
                                              key={attIndex}
                                              onClick={() => {
                                                if (isImage) {
                                                  setSelectedImage(attachment);
                                                  setShowImageModal(true);
                                                } else {
                                                  window.open(
                                                    attachment.urlPath,
                                                    "_blank"
                                                  );
                                                }
                                              }}
                                            >
                                              {isImage ? (
                                                <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                  <img
                                                    alt={
                                                      attachment.name ||
                                                      attachment.originalName
                                                    }
                                                    className="max-w-full max-h-full object-contain"
                                                    src={attachment.urlPath}
                                                  />
                                                </div>
                                              ) : (
                                                <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center p-2">
                                                  <i
                                                    className={`fas ${getFileIcon(extension)} text-2xl text-gray-400 mb-1`}
                                                  />
                                                  <span className="text-[10px] text-gray-600 dark:text-gray-400 text-center truncate w-full">
                                                    {attachment.name ||
                                                      attachment.originalName}
                                                  </span>
                                                </div>
                                              )}
                                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity flex items-center justify-center">
                                                <i className="fas fa-search-plus text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                )}

                              {/* Admin Signature */}
                              {(comment.author?.role === "admin" ||
                                comment.author_type === "admin" ||
                                comment.admin_id) && (
                                <div className="mt-6">
                                  <div className="w-full h-px bg-gray-200 dark:bg-gray-700 mb-3" />
                                  <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                      Lunova Player
                                    </div>
                                    <div className="flex flex-wrap gap-2 group">
                                      <a
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md transition-all duration-200 opacity-40 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white text-xs"
                                        href="https://play.google.com/store/apps/details?id=app.lunova.player&pli=1"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        <i className="fab fa-google-play" />
                                        <span>Google Play</span>
                                      </a>
                                      <a
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md transition-all duration-200 opacity-40 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white text-xs"
                                        href="https://apps.apple.com/us/app/lunova-player/id6751130474"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        <i className="fab fa-apple" />
                                        <span>App Store</span>
                                      </a>
                                      <a
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md transition-all duration-200 opacity-40 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white text-xs"
                                        href="https://www.amazon.com/dp/B0FCDR2S73/ref=sr_1_2?crid=EY4VGDY0VI7G&dib=eyJ2IjoiMSJ9.dCkVrLtiS5jwfQ6XsPFlZg.j8l0stHVluZo-CoVSDy1NRHe84Kd6ElB7mB_FpS7dNw&dib_tag=se&keywords=lunova+player&qid=1749581104&sprefix=lunova+player%2Caps%2C105&sr=8-2"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        <i className="fab fa-amazon" />
                                        <span>Amazon</span>
                                      </a>
                                      <a
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md transition-all duration-200 opacity-40 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white text-xs"
                                        href="https://lunova.app/download"
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        <i className="fas fa-download" />
                                        <span>Download</span>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              style={{ overflow: "visible" }}
              transition={{ delay: 0.2 }}
            >
              <h3
                className="font-semibold mb-4 p-3 text-center rounded-xl"
                style={{ background: "rgba(0, 0, 0, 0.15)" }}
              >
                Details
              </h3>
              <div className="space-y-4 relative">
                {/* Status */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    Status
                  </label>
                  <div className="relative mt-1" ref={statusDropdownRef}>
                    <button
                      className={`w-full text-left px-3 py-2 rounded-lg border ${
                        user?.role === "admin"
                          ? "cursor-pointer hover:border-gray-400"
                          : "cursor-default"
                      } border-gray-300 dark:border-gray-600`}
                      disabled={user?.role === "user"}
                      onClick={() =>
                        user?.role === "admin" &&
                        setShowStatusMenu(!showStatusMenu)
                      }
                    >
                      <StatusBadge status={ticket.status} />
                    </button>
                    {showStatusMenu && user?.role === "admin" && (
                      <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
                        {["open", "customer-replied", "answered", "closed"].map(
                          (status) => (
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                              key={status}
                              onClick={() => handleStatusChange(status)}
                            >
                              <StatusBadge status={status} />
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    Category
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <i
                      className={`fas ${getCategoryIcon(ticket.category)} text-gray-400`}
                    />
                    <span className="capitalize">
                      {ticket.category.replace("-", " ")}
                    </span>
                  </div>
                </div>

                {/* Assigned To - Admin Only */}
                {user?.role === "admin" && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Assigned To
                    </label>
                    <div className="relative mt-1" ref={assignDropdownRef}>
                      <button
                        className="w-full text-left px-3 py-2 rounded-lg border cursor-pointer hover:border-gray-400 border-gray-300 dark:border-gray-600"
                        onClick={() => setShowAssignMenu(!showAssignMenu)}
                      >
                        {ticket.assignedTo ? (
                          <div className="flex items-center gap-2">
                            {ticket.assignedTo.avatarUrl ? (
                              <img
                                alt={ticket.assignedTo.name || "Assigned"}
                                className="w-6 h-6 rounded-full object-cover"
                                src={ticket.assignedTo.avatarUrl}
                              />
                            ) : (
                              <div className="w-6 h-6 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center text-white text-xs">
                                {ticket.assignedTo.name?.charAt(0) || "U"}
                              </div>
                            )}
                            <span>{ticket.assignedTo.name || "Unknown"}</span>
                          </div>
                        ) : (
                          <span className="text-gray-500">Unassigned</span>
                        )}
                      </button>
                      {showAssignMenu && (
                        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-80 overflow-hidden">
                          <div className="max-h-80 overflow-y-auto rounded-lg">
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
                              onClick={() => handleAssigneeChange(null)}
                            >
                              <span className="text-gray-500">Unassigned</span>
                            </button>

                            {/* List all admins */}
                            {admins && admins.length > 0 ? (
                              <div>
                                <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-500 uppercase sticky top-0">
                                  Admins
                                </div>
                                {admins.map((admin, index) => (
                                  <button
                                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                      index === admins.length - 1
                                        ? "rounded-b-lg"
                                        : ""
                                    }`}
                                    key={admin.id}
                                    onClick={() => handleAssigneeChange(admin)}
                                  >
                                    <div className="flex items-center gap-2">
                                      {admin.avatarUrl ? (
                                        <img
                                          alt={admin.name || admin.email}
                                          className="w-6 h-6 rounded-full object-cover"
                                          src={admin.avatarUrl}
                                        />
                                      ) : (
                                        <div className="w-6 h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs">
                                          {admin.name
                                            ? admin.name.charAt(0).toUpperCase()
                                            : admin.email
                                                .charAt(0)
                                                .toUpperCase()}
                                        </div>
                                      )}
                                      <div>
                                        <div className="font-medium">
                                          {admin.name ||
                                            admin.email.split("@")[0]}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {admin.email}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center rounded-b-lg">
                                No admins available
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AI auto-reply (admin only) */}
                {user?.role === "admin" && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      AI auto-reply
                    </label>
                    <button
                      className="mt-1 w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:border-gray-400"
                      type="button"
                      onClick={handleAiToggle}
                    >
                      <span className="flex items-center gap-2">
                        <i className="fas fa-moon text-blue-500" />
                        {ticket.metadata?.ai?.enabled !== false
                          ? "On - Luna may reply"
                          : "Paused"}
                      </span>
                      <span
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          ticket.metadata?.ai?.enabled !== false
                            ? "bg-blue-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            ticket.metadata?.ai?.enabled !== false
                              ? "translate-x-4"
                              : "translate-x-1"
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                )}

                {/* Escalation state */}
                {ticket.metadata?.ai?.escalated && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm">
                    <i className="fas fa-user-headset" />
                    <span>Escalated to a human - Luna handed this off.</span>
                  </div>
                )}

                {/* Created */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    Created
                  </label>
                  <p className="mt-1">
                    {new Date(ticket.createdAt).toLocaleString()}
                  </p>
                </div>

                {/* Updated */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    Last Updated
                  </label>
                  <p className="mt-1">
                    {new Date(ticket.updatedAt).toLocaleString()}
                  </p>
                </div>

                {/* Close Ticket Button - Customer Only */}
                {user?.role !== "admin" && (
                  <div className="pt-2">
                    {ticket.metadata?.created_by_admin ? (
                      <div className="w-full p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-center">
                        <i className="fas fa-lock text-amber-600 dark:text-amber-400 mr-2" />
                        <span className="text-sm text-amber-700 dark:text-amber-300">
                          Only support staff can close this ticket
                        </span>
                      </div>
                    ) : (
                      <button
                        className={`w-full btn ${
                          ticket.status === "closed" || isImpersonating
                            ? "bg-gray-800 bg-opacity-40 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:bg-opacity-40"
                            : "bg-red-500 hover:bg-red-600 text-white"
                        }`}
                        disabled={ticket.status === "closed" || isImpersonating}
                        onClick={() => handleStatusChange("closed")}
                      >
                        <i className="fas fa-check-circle mr-2" />
                        {isImpersonating
                          ? "Return to Admin to Close"
                          : "Close Ticket"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Customer Info - Admin Only */}
            {user?.role === "admin" && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="card p-6 shadow-md"
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.3 }}
              >
                <h3
                  className="font-semibold mb-4 p-3 text-center rounded-xl"
                  style={{ background: "rgba(0, 0, 0, 0.15)" }}
                >
                  Customer
                </h3>
                <div
                  className="mb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg p-2 -m-2 transition-colors"
                  onClick={() => {
                    // Extract customer ID
                    const customerId = ticket.customer?.id;

                    // Check if this is the current logged-in user
                    if (user && user.id === customerId) {
                      // Navigate to profile page for current user
                      navigate("/profile");
                    } else if (customerId) {
                      // Navigate to customer details for other users
                      navigate(`/customers/${customerId}`);
                    } else {
                      console.error(
                        "No valid customer ID found in ticket:",
                        ticket.customer
                      );
                      toast.error("Unable to view customer profile");
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <CommentAvatar author={ticket.customer} isAdmin={false} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {ticket.customer?.name || "Unknown Customer"}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {ticket.customer?.email || "No email"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <button
                    className="w-full btn btn-ghost justify-start"
                    onClick={() => {
                      // Navigate to tickets page with registration_id filter
                      const registrationId =
                        ticket.registration_id ||
                        ticket.customer?.registration_id;

                      if (registrationId) {
                        navigate(`/tickets?registration_id=${registrationId}`);
                      } else {
                        toast.error(
                          "No registration ID found for this customer"
                        );
                      }
                    }}
                  >
                    <i className="fas fa-history" />
                    <span>Ticket History</span>
                    {typeof customerTicketCount === "number" && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {customerTicketCount}
                      </span>
                    )}
                  </button>
                  <button
                    className="w-full btn btn-ghost justify-start"
                    onClick={() => {
                      const email =
                        ticket.customer?.email || ticket.email || "";

                      if (email) {
                        setShowEmailModal(true);
                      } else {
                        toast.error("No email address found for this customer");
                      }
                    }}
                  >
                    <i className="fas fa-envelope" />
                    Send Email
                  </button>
                  <button
                    className="w-full btn btn-ghost justify-start"
                    disabled={!ticket.customer?.id}
                    onClick={() => {
                      if (!ticket.customer?.id) {
                        toast.error("No customer linked to this ticket");

                        return;
                      }
                      setShowCreateTicketModal(true);
                    }}
                  >
                    <i className="fas fa-plus-circle" />
                    Create New Ticket
                  </button>
                </div>
              </motion.div>
            )}

            {/* Tags */}
            {ticket.tags && ticket.tags.length > 0 && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="card p-6 shadow-md"
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="font-semibold mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {ticket.tags.map((tag, index) => (
                    <span className="badge badge-secondary" key={index}>
                      <i className="fas fa-tag mr-1" />
                      {tag}
                    </span>
                  ))}
                  {user?.role === "admin" && (
                    <button className="badge badge-ghost hover:badge-secondary">
                      <i className="fas fa-plus" />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              onClick={() => setShowImageModal(false)}
            >
              <i className="fas fa-times text-2xl" />
            </button>
            <img
              alt={
                selectedImage.name || selectedImage.originalName || "Attachment"
              }
              className="max-w-full max-h-full object-contain"
              src={selectedImage.urlPath}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-4 right-4 text-center text-white bg-black bg-opacity-50 py-2 px-4 rounded">
              {selectedImage.name || selectedImage.originalName || "Attachment"}
              {selectedImage.extension}
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      <SendEmailModal
        defaultSubject={
          ticket ? `Re: Ticket #${ticket.ticketNumber} - ${ticket.subject}` : ""
        }
        defaultTo={ticket?.customer?.email || ticket?.email || ""}
        isOpen={showEmailModal}
        ticketId={id}
        onClose={() => setShowEmailModal(false)}
      />

      {/* Create New Ticket Modal (admin creating on behalf of this customer) */}
      <NewTicketModal
        isOpen={showCreateTicketModal}
        prefilledCustomer={
          ticket?.customer
            ? {
                id: ticket.customer.id,
                name: ticket.customer.name,
                email: ticket.customer.email,
              }
            : null
        }
        onClose={() => setShowCreateTicketModal(false)}
        onTicketCreated={() => {
          setCustomerTicketCount((prev) =>
            typeof prev === "number" ? prev + 1 : prev
          );
        }}
      />
    </>
  );
};

export default TicketDetail;
